import { useCallback, useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button } from "@mui/material";
import { Popconfirm } from "antd";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { AuthContext } from "../../../shared/context/auth-context";
import { useLoading } from "../../../shared/context/LoadingContext";
import NotaPartilhaUnidades from "../components/NotaPartilhaUnidades";

import "./MarcacaoOnline.scss";

/**
 * O que o paciente escolhe na página pública, antes do horário.
 *
 * ─── PORQUE A ESCOLHA VEM ANTES DO CALENDÁRIO ───────────────────────────────
 *
 * Porque é ela que decide quanto tempo o procedimento ocupa. Uma extração leva
 * 30 minutos e a agenda anda de hora a hora: sem saber ao que a pessoa vem,
 * a página gasta uma hora inteira num procedimento de meia.
 *
 * ─── O QUE ESTE ECRÃ NÃO FAZ ────────────────────────────────────────────────
 *
 * Não liga nem desliga a página, e não muda o endereço dela.
 *
 * Ligar exige decidir, dentista a dentista, quem entra na agenda pública —
 * quem marca consultas por fora aparece sempre livre, e a página passa a
 * prometer horas que não existem. Mudar o endereço parte todos os links já
 * distribuídos, incluindo o da resposta automática do WhatsApp. Nenhuma das
 * duas cabe num interruptor; fazem-se com quem gere o servidor.
 */

/* Pela ordem do `Date.getDay()`: 0=domingo. É o mesmo índice que o servidor
   guarda e que o helper compara, para não haver conversão pelo caminho — e
   portanto nenhum sítio onde a segunda-feira se transforme em domingo. */
const DIAS_DA_SEMANA = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
];

const MarcacaoOnline = ({ sendRequest, clinicaId }) => {
  const auth = useContext(AuthContext);
  const { startLoading, stopLoading } = useLoading();

  const unidadeId = auth.estabelecimento?._id ?? null;

  const [unidade, setUnidade] = useState(null);
  const [tratamentos, setTratamentos] = useState([]);
  const [dentistas, setDentistas] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [dias, setDias] = useState(30);
  const [maxSlots, setMaxSlots] = useState(6);
  const [aGuardar, setAGuardar] = useState(false);

  /* A página ligada e quem alimenta a agenda pública. Vivem aqui, ao lado do
     resto, porque passaram a guardar-se no mesmo botão — antes ligar a página
     era trabalho de um script no servidor. */
  const [ligada, setLigada] = useState(false);
  const [publicos, setPublicos] = useState([]);

  /* O link vem montado do servidor: é lá que vive o endereço do portal, e ele
     muda entre o ambiente de teste e a produção. */
  const [linkPublico, setLinkPublico] = useState(null);
  const [copiado, setCopiado] = useState(false);

  const carregar = useCallback(async () => {
    if (!unidadeId) return;
    startLoading();
    try {
      const r = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/estabelecimentos/${unidadeId}/marcacaopublica`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );
      setUnidade(r.unidade);
      setLinkPublico(r.linkPublico || null);
      setTratamentos(r.tratamentos || []);
      setDentistas(r.dentistas || []);
      const mp = r.unidade?.marcacaoPublica || {};
      setDias(mp.diasVisiveis ?? 30);
      setMaxSlots(mp.maxSlotsPorDia ?? 6);
      setLigada(!!mp.ativo);
      setPublicos(
        (r.dentistas || [])
          .filter((d) => d.agendaPublica)
          .map((d) => String(d._id)),
      );
      setMotivos(
        [...(mp.motivos || [])]
          .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
          .map((m) => ({
            ...m,
            tratamento: m.tratamento ? String(m.tratamento) : "",
            dentistas: (m.dentistas || []).map(String),
            /* Números, para o `includes` das caixas dos dias bater — um "2"
               vindo da base nunca igualava o 2 do ecrã, e a terça aparecia
               desligada mesmo estando gravada. */
            diasSemana: (m.diasSemana || []).map(Number),
            turno: m.turno || "qualquer",
          })),
      );
    } catch (err) {
      console.error("err", err);
    } finally {
      stopLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.token, sendRequest, unidadeId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const mexer = (i, campo, valor) =>
    setMotivos((atual) =>
      atual.map((m, j) => (j === i ? { ...m, [campo]: valor } : m)),
    );

  const trocar = (i, j) =>
    setMotivos((atual) => {
      if (j < 0 || j >= atual.length) return atual;
      const copia = [...atual];
      [copia[i], copia[j]] = [copia[j], copia[i]];
      return copia;
    });

  const acrescentar = () =>
    setMotivos((atual) => [
      ...atual,
      {
        etiqueta: "",
        ajuda: "",
        tratamento: "",
        dentistas: [],
        duracaoMinutos: 30,
        passoMinutos: 30,
        /* Vazio = todos os dias, e «qualquer» = manhã e tarde. Um motivo novo
           não deve nascer com restrições que ninguém pediu. */
        diasSemana: [],
        turno: "qualquer",
        tipo: "marcacao",
        pedeDescricao: false,
        prioritario: false,
        ativo: true,
      },
    ]);

  /* `navigator.clipboard` só existe em HTTPS (e em localhost). Num acesso por
     IP ou http simples não há — daí a queda para a seleção do texto, que
     deixa a pessoa copiar com Ctrl+C em vez de ficar sem nada. */
  const copiarLink = async () => {
    if (!linkPublico) return;
    try {
      await navigator.clipboard.writeText(linkPublico);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      const el = document.querySelector(".marcacao-online__link-endereco");
      if (el) {
        const intervalo = document.createRange();
        intervalo.selectNodeContents(el);
        const selecao = window.getSelection();
        selecao.removeAllRanges();
        selecao.addRange(intervalo);
      }
      toast("Copie com Ctrl+C — o link já está selecionado.");
    }
  };

  const guardar = async () => {
    /* As mesmas verificações do servidor, aqui à frente. Não é segurança — o
       servidor recusa à mesma — é não obrigar a pessoa a carregar em Guardar
       para descobrir que escreveu um nome vazio. */
    const semNome = motivos.findIndex((m) => !String(m.etiqueta).trim());
    if (semNome >= 0) {
      toast.error(`O motivo ${semNome + 1} está sem nome.`);
      return;
    }

    if (ligada && publicos.length === 0) {
      toast.error(
        "Escolha pelo menos um médico para a agenda pública antes de ligar a página.",
      );
      return;
    }

    setAGuardar(true);
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/estabelecimentos/${unidadeId}/marcacaopublica`,
        "PATCH",
        JSON.stringify({
          ativo: ligada,
          dentistasPublicos: publicos,
          diasVisiveis: Number(dias),
          maxSlotsPorDia: Number(maxSlots),
          motivos: motivos.map((m) => ({
            ...m,
            tratamento: m.tratamento || undefined,
            /* Um motivo não pode apontar para quem saiu da agenda pública:
               ficava sem horários nenhuns, e sem erro — o pior dos dois. */
            dentistas: (m.dentistas || []).filter((id) =>
              publicos.includes(String(id)),
            ),
          })),
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );
      toast.success("Guardado.");
      carregar();
    } catch (err) {
      toast.error(err.message || "Não foi possível guardar.");
    } finally {
      setAGuardar(false);
    }
  };

  if (!unidadeId) {
    return (
      <p className="marcacao-online__aviso">
        Esta sessão não tem unidade. Escolha uma no indicador junto ao seu nome.
      </p>
    );
  }

  /* O que está GRAVADO, que não é o mesmo que o interruptor no ecrã: o
     endereço só existe depois de a página ter sido ligada e guardada. */
  const ligadaGravada = !!unidade?.marcacaoPublica?.ativo;
  const slug = unidade?.marcacaoPublica?.slug;

  /* Quem pode ser atribuído a um motivo: só quem está na agenda pública.
     Atribuir um motivo a alguém de fora dela deixava-o sem horários. Calcula-se
     uma vez e não dentro do `map` dos motivos, que repetia o filtro por cada
     motivo no ecrã. */
  const quemPodeFazer = dentistas.filter((d) =>
    publicos.includes(String(d._id)),
  );

  return (
    <div className="marcacao-online">
      <ul className="marcacao-online__notas">
        <NotaPartilhaUnidades porUnidade />
        {!ligadaGravada && (
          <li>
            A página está <strong>desligada</strong>. O que configurar aqui fica
            guardado e passa a aparecer assim que a ligar aqui em baixo.
          </li>
        )}
      </ul>

      {/* ── O link para dar ao doente ─────────────────────────────────────
          Em destaque e inteiro, porque é para ser copiado e mandado. Mostrar
          só o caminho obrigava quem o quisesse usar a saber de cor o endereço
          do portal. */}
      {ligadaGravada && slug && (
        <div className="marcacao-online__link">
          <span className="marcacao-online__link-rotulo">
            Link para dar aos pacientes
          </span>

          {linkPublico ? (
            <div className="marcacao-online__link-caixa">
              <a
                href={linkPublico}
                target="_blank"
                rel="noreferrer"
                className="marcacao-online__link-endereco"
              >
                {linkPublico}
              </a>
              <button
                type="button"
                className="marcacao-online__link-copiar"
                onClick={copiarLink}
              >
                {copiado ? "Copiado" : "Copiar"}
              </button>
            </div>
          ) : (
            <>
              <code className="marcacao-online__link-endereco">
                /marcacao/{slug}
              </code>
              <p className="marcacao-online__ajuda">
                Falta configurar o endereço do portal no servidor
                (<code>PORTAL_URL_PUBLICO</code>) para se poder mostrar o link
                completo. Até lá fica só o caminho.
              </p>
            </>
          )}

          <p className="marcacao-online__ajuda">
            Os caracteres no fim não são enfeite: sem eles, quem descobrisse o
            link de uma clínica descobria o de todas, e com ele a ocupação da
            agenda de cada uma.
          </p>
        </div>
      )}

      {/* ── Ligar a página ────────────────────────────────────────────────
          O endereço não se edita: é gerado ao ligar pela primeira vez, e
          mudá-lo parte todos os links já distribuídos. Desligar guarda-o. */}
      <div className="marcacao-online__ligar">
        <label className="marcacao-online__interruptor">
          <input
            type="checkbox"
            checked={ligada}
            onChange={(e) => setLigada(e.target.checked)}
          />
          <span>Página de marcações aberta ao público</span>
        </label>

        <p className="marcacao-online__ajuda">
          Quem tiver o endereço pode pedir consulta sem entrar no sistema. O
          pedido não ocupa horário nenhum: fica pendente até alguém o aprovar.
        </p>

        <fieldset className="marcacao-online__dentistas">
          <legend>Quem alimenta a agenda pública</legend>
          <p className="marcacao-online__ajuda">
            Os horários livres saem das agendas destes médicos.{" "}
            <strong>
              Escolha só quem mantém mesmo a agenda no sistema
            </strong>{" "}
            — quem combina consultas por fora aparece sempre livre, e a página
            passa a anunciar horas que não existem.
          </p>

          {dentistas.length === 0 && (
            <p className="marcacao-online__aviso">
              Esta unidade não tem médicos.
            </p>
          )}

          {dentistas.map((d) => {
            const id = String(d._id);
            return (
              <label key={id} className="marcacao-online__dentista">
                <input
                  type="checkbox"
                  checked={publicos.includes(id)}
                  onChange={(e) =>
                    setPublicos((atual) =>
                      e.target.checked
                        ? [...atual, id]
                        : atual.filter((x) => x !== id),
                    )
                  }
                />
                <span>{d.name}</span>
              </label>
            );
          })}

          <p className="marcacao-online__ajuda">
            Esta escolha vale para o médico em toda a clínica, não só nesta
            unidade.
          </p>
        </fieldset>
      </div>

      <div className="marcacao-online__opcoes">
        <label>
          <span>Dias visíveis</span>
          <input
            type="number"
            min={1}
            max={90}
            value={dias}
            onChange={(e) => setDias(e.target.value)}
          />
          <small>Até quantos dias para a frente a página mostra.</small>
        </label>
        <label>
          <span>Horários por dia</span>
          <input
            type="number"
            min={1}
            max={40}
            value={maxSlots}
            onChange={(e) => setMaxSlots(e.target.value)}
          />
          <small>
            Mostrar todos faz uma agenda vazia anunciar centenas de horas — o
            que não é credível e convida a faltas.
          </small>
        </label>
      </div>

      <div className="marcacao-online__cabecalho">
        <h3>O que o paciente pode escolher</h3>
        <Button variant="outlined" onClick={acrescentar} startIcon={<Plus size={16} />}>
          Acrescentar
        </Button>
      </div>

      {motivos.length === 0 && (
        <p className="marcacao-online__vazio">
          Sem opções configuradas, a página pede logo o horário, sem perguntar
          ao que a pessoa vem.
        </p>
      )}

      {motivos.map((m, i) => (
        <div
          key={i}
          className={`marcacao-online__motivo${
            m.ativo === false ? " marcacao-online__motivo--inativo" : ""
          }`}
        >
          <div className="marcacao-online__ordem">
            <button type="button" onClick={() => trocar(i, i - 1)} disabled={i === 0}>
              <ArrowUp size={16} />
            </button>
            <span>{i + 1}</span>
            <button
              type="button"
              onClick={() => trocar(i, i + 1)}
              disabled={i === motivos.length - 1}
            >
              <ArrowDown size={16} />
            </button>
          </div>

          <div className="marcacao-online__campos">
            <label className="marcacao-online__largo">
              <span>O que o paciente lê</span>
              <input
                value={m.etiqueta}
                maxLength={60}
                placeholder="Extração"
                onChange={(e) => mexer(i, "etiqueta", e.target.value)}
              />
            </label>

            <label className="marcacao-online__largo">
              <span>Ajuda (opcional)</span>
              <input
                value={m.ajuda || ""}
                maxLength={140}
                placeholder="Dente partido, a abanar, ou com indicação para tirar"
                onChange={(e) => mexer(i, "ajuda", e.target.value)}
              />
            </label>

            <label>
              <span>Tipo</span>
              <select value={m.tipo} onChange={(e) => mexer(i, "tipo", e.target.value)}>
                <option value="marcacao">Escolhe horário</option>
                <option value="urgencia">Urgência — deixa contacto</option>
                <option value="contacto">Só mostra o telefone</option>
              </select>
            </label>

            {m.tipo === "marcacao" && (
              <>
                <label>
                  <span>Tratamento</span>
                  {/* A etiqueta é separada do tratamento de propósito: a
                      clínica escolhe «Exodontia simples» e o paciente lê
                      «Extração». Ninguém de fora sabe o que é uma exodontia. */}
                  <select
                    value={m.tratamento || ""}
                    onChange={(e) => mexer(i, "tratamento", e.target.value)}
                  >
                    <option value="">— nenhum —</option>
                    {tratamentos.map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.designacao}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <span>Duração (min)</span>
                  <input
                    type="number"
                    min={5}
                    max={240}
                    value={m.duracaoMinutos}
                    onChange={(e) => mexer(i, "duracaoMinutos", Number(e.target.value))}
                  />
                </label>

                <label>
                  <span>Intervalo (min)</span>
                  <input
                    type="number"
                    min={5}
                    max={240}
                    value={m.passoMinutos}
                    onChange={(e) => mexer(i, "passoMinutos", Number(e.target.value))}
                  />
                  <small>
                    De quanto em quanto se oferece um início. Com duração 30 e
                    intervalo 30, cabem duas numa hora.
                  </small>
                </label>

                {/* Pôr aqui alguém restringe os horários à agenda dessa
                    pessoa. É assim que «só se extrai às terças» se resolve: a
                    terça é o dia em que ela trabalha, e o horário sai de onde a
                    verdade está.

                    Caixas e não um `<select multiple>`: no controlo nativo,
                    escolher dois exige Ctrl+clique — que ninguém adivinha — e um
                    clique normal apaga a seleção anterior sem avisar. Quem
                    escolhesse duas médicas ficava com uma e não perceberia
                    porquê. É também o molde que a secção «Quem alimenta a
                    agenda pública» já usa, nesta mesma página.

                    Não é um `<label>` a envolver tudo porque cada caixa traz o
                    seu — e um `<label>` dentro de outro é HTML inválido. */}
                <div className="marcacao-online__quem-faz">
                  <span>Quem faz (nenhum = qualquer um)</span>

                  {quemPodeFazer.map((d) => {
                    const id = String(d._id);
                    const faz = (m.dentistas || []).includes(id);
                    return (
                      <label key={id} className="marcacao-online__dentista">
                        <input
                          type="checkbox"
                          checked={faz}
                          onChange={() =>
                            mexer(
                              i,
                              "dentistas",
                              faz
                                ? (m.dentistas || []).filter((x) => x !== id)
                                : [...(m.dentistas || []), id],
                            )
                          }
                        />
                        <span>{d.name}</span>
                      </label>
                    );
                  })}

                  {/* A condição é a lista FILTRADA e não `dentistas`: uma
                      unidade pode ter médicos e nenhum deles na agenda pública,
                      e era esse o caso que o aviso antigo deixava passar calado
                      — mostrava uma lista vazia sem dizer porquê. */}
                  {quemPodeFazer.length === 0 && (
                    <small>
                      Nenhum médico está na agenda pública (ver acima). Enquanto
                      assim for, a página não mostra horários.
                    </small>
                  )}
                </div>

                {/* QUANDO se aceita este motivo — pergunta diferente da de
                    cima. Lá é «quem está disponível»; aqui é «quando aceitamos
                    este pedido pela página». Uma médica pode trabalhar a semana
                    toda e a clínica querer avaliações online só às terças,
                    deixando o resto para quem já é cliente.

                    É um filtro: nunca acrescenta horários, só tira dos que a
                    agenda tem. Se ela deixar de trabalhar às terças, as terças
                    passam a dar zero — ao contrário de um horário próprio, que
                    continuaria a anunciar um dia que já não existe. */}
                <div className="marcacao-online__quem-faz">
                  <span>Em que dias (nenhum = todos)</span>

                  <div className="marcacao-online__dias">
                    {DIAS_DA_SEMANA.map((nome, d) => {
                      const aceita = (m.diasSemana || []).includes(d);
                      return (
                        <label
                          key={d}
                          className="marcacao-online__dentista"
                          title={nome}
                        >
                          <input
                            type="checkbox"
                            checked={aceita}
                            onChange={() =>
                              mexer(
                                i,
                                "diasSemana",
                                aceita
                                  ? (m.diasSemana || []).filter((x) => x !== d)
                                  : [...(m.diasSemana || []), d].sort(
                                      (a, b) => a - b,
                                    ),
                              )
                            }
                          />
                          <span>{nome.slice(0, 3)}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <label>
                  <span>Turno</span>
                  <select
                    value={m.turno || "qualquer"}
                    onChange={(e) => mexer(i, "turno", e.target.value)}
                  >
                    <option value="qualquer">Manhã e tarde</option>
                    <option value="manha">Só de manhã</option>
                    <option value="tarde">Só de tarde</option>
                  </select>
                  {/* Pela hora de início, e não pela de fim: senão uma consulta
                      de uma hora às 11:30 deixava de ser «de manhã». */}
                  <small>Conta a hora a que a consulta começa.</small>
                </label>
              </>
            )}

            <label className="marcacao-online__caixa">
              <input
                type="checkbox"
                checked={!!m.pedeDescricao}
                onChange={(e) => mexer(i, "pedeDescricao", e.target.checked)}
              />
              <span>Pede uma descrição ao paciente</span>
            </label>

            <label className="marcacao-online__caixa">
              <input
                type="checkbox"
                checked={!!m.prioritario}
                onChange={(e) => mexer(i, "prioritario", e.target.checked)}
              />
              <span>Avisa a equipa e fica no topo da lista</span>
            </label>

            <label className="marcacao-online__caixa">
              <input
                type="checkbox"
                checked={m.ativo !== false}
                onChange={(e) => mexer(i, "ativo", e.target.checked)}
              />
              <span>Mostrar na página</span>
            </label>
          </div>

          <Popconfirm
            title="Remover esta opção?"
            description="Os pedidos já feitos com ela não mudam."
            icon={<InfoOutlinedIcon style={{ color: "#dc2626" }} />}
            okText="Remover"
            cancelText="Cancelar"
            onConfirm={() => setMotivos((a) => a.filter((_, j) => j !== i))}
          >
            <button type="button" className="marcacao-online__remover">
              <Trash2 size={16} />
            </button>
          </Popconfirm>
        </div>
      ))}

      <div className="marcacao-online__acoes">
        <Button variant="contained" onClick={guardar} disabled={aGuardar}>
          {aGuardar ? "A guardar…" : "Guardar"}
        </Button>
      </div>
    </div>
  );
};

export default MarcacaoOnline;
